import { clear, el } from './dom';
import { icon } from './Icons';
import { keyCaps } from './ToolHint';
import {
  TOUR_STEPS,
  anchorFor,
  audienceFor,
  markTourSeen,
  placeCard,
  progressStyle,
  readTourMemory,
  resolveTour,
  shouldOfferTour,
  spotlightRect,
  stepProgress,
  writeTourMemory,
  type Rect,
  type TourAudience,
  type TourStep,
} from './Tour';

/**
 * The tutorial overlay: a scrim over the app with a hole cut around the control being described,
 * and a card beside it.
 *
 * ## The hole is a shadow, not a hole
 *
 * There is one lit element, and the entire dimming is its own `box-shadow` spread far enough to
 * cover any window. That is worth the trick: the alternative — four panes around the anchor, or an
 * SVG mask — either loses the rounded corner or has to be rebuilt on every step, whereas one
 * element with a transform and a size transitions from step to step for free, which is exactly the
 * movement that makes a tour legible. The scrim colour is on the shadow, so nothing is layered over
 * the anchor and it keeps its own colour rather than being tinted by a translucent sheet.
 *
 * ## It blocks the app while it is up
 *
 * The overlay takes pointer input across the whole window, including over the lit control. Most web
 * tours let you click the thing they are pointing at; this one deliberately does not, because the
 * thing behind the overlay is a 3D viewport where a stray click plants a tree, and because a tour
 * that can be half-driven leaves the reader unsure whether they are meant to be doing something.
 * It is a two-minute read with a visible exit, not a task list.
 *
 * ## Every step has three ways out
 *
 * The close button, the Skip link, and Escape. Whichever is used, the audience is recorded as
 * having seen it, so it never re-raises itself unasked — see `Tour.shouldOfferTour`. Reaching the
 * last card and pressing Done is recorded identically: a tutorial that distinguishes "finished"
 * from "escaped" only in order to keep offering itself to the second group is a tutorial that has
 * mistaken its own completion rate for the user's interest.
 *
 * ## Steps that point at nothing are not shown
 *
 * The script is filtered through `resolveTour` against what is mounted right now, so a standalone
 * build with no account control never sees the account card, and the count on every card ("3 of
 * 10") is the count of what this reader will actually be shown.
 */

export interface TourOverlayDeps {
  /** Where the overlay mounts. Its own layer on `<body>`, above the HUD and the dialogs. */
  container?: HTMLElement;
  /** Searched for anchors. Defaults to the document, so any mounted control can be pointed at. */
  root?: ParentNode;
  /** Called once the layer is gone, however it went. */
  onClose?: (completed: boolean) => void;
  /** Overrides the script. Injected by tests; production takes {@link TOUR_STEPS}. */
  steps?: readonly TourStep[];
}

/** Padding around the anchor inside the lit rectangle. */
const SPOT_PADDING = 8;

export class TourOverlay {
  readonly root: HTMLElement;

  private readonly spot: HTMLElement;
  private readonly card: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly bodyEl: HTMLElement;
  private readonly keysEl: HTMLElement;
  private readonly dotsEl: HTMLElement;
  private readonly progressEl: HTMLElement;
  private readonly backButton: HTMLButtonElement;
  private readonly nextButton: HTMLButtonElement;

  private readonly steps: readonly TourStep[];
  private readonly searchRoot: ParentNode;
  /** Watches the page's own box, so a dock opening under the tour moves the spotlight with it. */
  private readonly observer: ResizeObserver | null;
  private readonly returnFocus: Element | null;
  private index = 0;
  private disposed = false;
  private frame: number | null = null;

  constructor(private readonly deps: TourOverlayDeps = {}) {
    this.searchRoot = deps.root ?? document;
    this.steps = resolveTour(deps.steps ?? TOUR_STEPS, (selector) => this.present(selector));
    this.returnFocus = document.activeElement;

    this.spot = el('div', { class: 'lg-tour__spot', attrs: { 'aria-hidden': 'true' } });

    this.titleEl = el('h2', { class: 'lg-tour__title', attrs: { id: 'lg-tour-title' } });
    this.bodyEl = el('p', { class: 'lg-tour__body' });
    this.keysEl = el('dl', { class: 'lg-tour__keys' });
    this.dotsEl = el('div', { class: 'lg-tour__dots', attrs: { 'aria-hidden': 'true' } });
    this.progressEl = el('span', {
      class: 'lg-tour__progress',
      // Polite rather than assertive: it is a position report, not an alert, and it must not talk
      // over the title and body the reader was actually moved here to hear.
      attrs: { role: 'status', 'aria-live': 'polite' },
    });

    this.backButton = el(
      'button',
      {
        class: 'lg-button lg-tour__back',
        attrs: { type: 'button' },
        on: { click: () => this.go(this.index - 1) },
      },
      el('span', { text: 'Back' }),
    );
    this.nextButton = el(
      'button',
      {
        class: 'lg-button lg-button--primary lg-tour__next',
        attrs: { type: 'button' },
        on: { click: () => this.advance() },
      },
      el('span', { text: 'Next' }),
    );

    this.card = el(
      'div',
      {
        class: 'lg-tour__card',
        attrs: {
          role: 'dialog',
          'aria-modal': 'true',
          'aria-labelledby': 'lg-tour-title',
        },
      },
      el(
        'div',
        { class: 'lg-tour__head' },
        el('span', { class: 'lg-tour__mark', html: icon('plant', 15) }),
        this.progressEl,
        el('button', {
          class: 'lg-iconbtn lg-tour__close',
          html: icon('close', 15),
          title: 'Close the tour',
          attrs: { type: 'button', 'aria-label': 'Close the tour' },
          on: { click: () => this.finish(false) },
        }),
      ),
      this.titleEl,
      this.bodyEl,
      this.keysEl,
      el(
        'div',
        { class: 'lg-tour__foot' },
        this.dotsEl,
        el(
          'div',
          { class: 'lg-tour__actions' },
          el('button', {
            class: 'lg-tour__skip',
            text: 'Skip tour',
            attrs: { type: 'button' },
            on: { click: () => this.finish(false) },
          }),
          this.backButton,
          this.nextButton,
        ),
      ),
    );

    this.root = el(
      'div',
      { class: 'lg-tour', dataset: { side: 'center' } },
      this.spot,
      this.card,
    );
    this.root.addEventListener('keydown', this.onKeyDown);
    // The pointer is taken across the whole layer; a click on the scrim does nothing on purpose,
    // so a misplaced click cannot lose someone their place halfway through.
    this.root.addEventListener('pointerdown', (event) => {
      if (!this.card.contains(event.target as Node)) event.preventDefault();
    });

    (deps.container ?? document.body).appendChild(this.root);
    globalThis.addEventListener?.('resize', this.onReflow);
    // Anchors move when a dock collapses or a panel opens; watching the HUD's own box catches the
    // layout changes that matter without polling.
    this.observer = createObserver(this.onReflow);
    this.observer?.observe(document.body);

    this.render();
    this.nextButton.focus();
  }

  /** How many steps this reader will actually be shown, after filtering. */
  get length(): number {
    return this.steps.length;
  }

  /** The step on screen, for tests and for anything that wants to resume where someone left. */
  get currentStep(): TourStep | null {
    return this.steps[this.index] ?? null;
  }

  private advance(): void {
    if (this.index >= this.steps.length - 1) {
      this.finish(true);
      return;
    }
    this.go(this.index + 1);
  }

  private go(index: number): void {
    const next = Math.max(0, Math.min(index, this.steps.length - 1));
    if (next === this.index) return;
    this.index = next;
    this.render();
  }

  private render(): void {
    const step = this.currentStep;
    // Only reachable with an injected script that filtered down to nothing. The caller is left to
    // decide what to do about that — see `tourController` — rather than the layer closing itself
    // out from under a constructor that has not returned yet.
    if (!step) return;

    this.titleEl.textContent = step.title;
    this.bodyEl.textContent = step.body;

    clear(this.keysEl);
    for (const key of step.keys ?? []) {
      this.keysEl.appendChild(
        el(
          'div',
          { class: 'lg-tour__keyrow' },
          el('dt', { class: 'lg-tour__keycaps', html: keyCaps(key.caps) }),
          el('dd', { class: 'lg-tour__keytext', text: key.text }),
        ),
      );
    }
    this.keysEl.hidden = (step.keys ?? []).length === 0;

    clear(this.dotsEl);
    this.dotsEl.dataset['style'] = progressStyle(this.steps.length);
    if (progressStyle(this.steps.length) === 'bar') {
      // One filled span in a track, widthed by fraction. `index + 1` so the first step already
      // shows progress: a bar that reads empty on the card you are looking at is a bar that has
      // not counted the step you are on.
      const done = ((this.index + 1) / this.steps.length) * 100;
      this.dotsEl.appendChild(
        el('span', { class: 'lg-tour__fill', style: { width: `${String(done)}%` } }),
      );
    } else {
      this.steps.forEach((_step, i) => {
        this.dotsEl.appendChild(
          el('span', {
            class: 'lg-tour__dot',
            dataset: { state: i === this.index ? 'here' : i < this.index ? 'past' : 'ahead' },
          }),
        );
      });
    }

    this.progressEl.textContent = stepProgress(this.index, this.steps.length);
    this.backButton.disabled = this.index === 0;
    const last = this.index === this.steps.length - 1;
    this.nextButton.replaceChildren(el('span', { text: last ? 'Done' : 'Next' }));

    // Synchronously, and cancelling any reflow already queued: see {@link position}. The card and
    // the spotlight have to change in the same frame or they contradict each other.
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.layout();
  }

  /**
   * Measure and place, batched to a frame.
   *
   * This is the path for *reflows* — a window resize, a dock opening under the overlay — which can
   * arrive in bursts, so they are coalesced into one measurement per frame.
   *
   * Advancing a step deliberately does not come through here; it calls {@link layout} directly. The
   * card's new text is committed to the DOM synchronously, and the `getBoundingClientRect` in
   * `layout` forces the style and layout recalc that makes the new height readable, so there is
   * nothing a frame's delay buys. What it costs is a window — one frame wide on a fast machine and
   * arbitrarily wide on a busy one — in which the card says one thing and the spotlight is still
   * lighting the previous step's control, which is the single most confusing state this overlay
   * has.
   */
  private position(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (this.disposed) return;
      this.layout();
    });
  }

  private layout(): void {
    const step = this.currentStep;
    if (!step) return;

    const viewport = {
      width: globalThis.innerWidth || document.documentElement.clientWidth,
      height: globalThis.innerHeight || document.documentElement.clientHeight,
    };
    const selector = anchorFor(step, (value) => this.present(value));
    const anchor = selector === null ? null : this.rectOf(selector);

    if (anchor === null) {
      // No anchor, or it went away between the filter and now: the scrim covers everything evenly
      // and the card is centred. Sized to nothing rather than hidden, so the shadow still paints.
      this.spot.dataset['lit'] = 'no';
      this.spot.style.width = '0px';
      this.spot.style.height = '0px';
      this.spot.style.transform = `translate(${String(Math.round(viewport.width / 2))}px, ${String(
        Math.round(viewport.height / 2),
      )}px)`;
    } else {
      const lit = spotlightRect(anchor, SPOT_PADDING, {
        left: 0,
        top: 0,
        width: viewport.width,
        height: viewport.height,
      });
      this.spot.dataset['lit'] = 'yes';
      this.spot.style.width = `${String(Math.round(lit.width))}px`;
      this.spot.style.height = `${String(Math.round(lit.height))}px`;
      this.spot.style.transform = `translate(${String(Math.round(lit.left))}px, ${String(
        Math.round(lit.top),
      )}px)`;
    }

    const box = this.card.getBoundingClientRect();
    const placement = placeCard({
      anchor,
      card: { width: box.width, height: box.height },
      viewport,
      ...(step.side ? { side: step.side } : {}),
    });

    this.card.style.left = `${String(placement.left)}px`;
    this.card.style.top = `${String(placement.top)}px`;
    this.root.dataset['side'] = placement.side;
    if (placement.arrow === null) {
      this.card.style.removeProperty('--lg-tour-arrow');
      this.card.dataset['arrow'] = 'off';
    } else {
      this.card.style.setProperty('--lg-tour-arrow', `${String(placement.arrow)}px`);
      this.card.dataset['arrow'] = 'on';
    }
  }

  private readonly onReflow = (): void => {
    if (this.disposed) return;
    this.position();
  };

  /**
   * The keyboard: arrows and Enter walk the tour, Escape leaves it, Tab is kept inside the card.
   *
   * The trap is three focusable controls in one card, so it is done by hand rather than by pulling
   * in a focus library — and it has to be done at all, because everything behind the overlay is
   * still in the tab order and reachable while being unclickable, which is the worst of both.
   */
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.finish(false);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.advance();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index - 1);
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...this.card.querySelectorAll<HTMLElement>('button:not([disabled])')];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.card.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** Present means: it is in the tree, and it has a box. An empty mount slot is neither. */
  private present(selector: string): boolean {
    const found = this.searchRoot.querySelector(selector);
    if (!(found instanceof HTMLElement)) return false;
    const box = found.getBoundingClientRect();
    return box.width > 0 && box.height > 0;
  }

  private rectOf(selector: string): Rect | null {
    const found = this.searchRoot.querySelector(selector);
    if (!(found instanceof HTMLElement)) return null;
    const box = found.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return null;
    return { left: box.left, top: box.top, width: box.width, height: box.height };
  }

  /** Leave, by any of the three doors. `completed` is reported, not acted on. */
  private finish(completed: boolean): void {
    this.dispose();
    this.deps.onClose?.(completed);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    globalThis.removeEventListener?.('resize', this.onReflow);
    this.observer?.disconnect();
    this.root.remove();
    // Focus goes back where it came from, so leaving the tour with the keyboard does not dump the
    // caret on <body> and cost someone their place in the toolbar.
    if (this.returnFocus instanceof HTMLElement && this.returnFocus.isConnected) {
      this.returnFocus.focus();
    }
  }
}

function createObserver(onChange: () => void): ResizeObserver | null {
  if (typeof ResizeObserver === 'undefined') return null;
  return new ResizeObserver(() => onChange());
}

/* ---------------------------------- Starting it, once ---------------------------------- */

export interface TourController {
  /** Open it now, whoever has seen what. Used by the ⓘ sheet's "Take the tour". */
  start(): void;
  /** Open it only if this audience has never been offered it. */
  offer(audience: TourAudience): void;
  dispose(): void;
}

/**
 * One tour at a time, remembered per account.
 *
 * The controller is what the application holds: it owns the "has this person seen it" bookkeeping
 * so neither the overlay nor the HUD has to, and it guarantees a second call cannot stack two
 * overlays on top of each other — which is otherwise exactly what happens when someone presses
 * "Take the tour" while one is already up.
 */
export function tourController(deps: Omit<TourOverlayDeps, 'onClose'> = {}): TourController {
  let live: TourOverlay | null = null;
  let audience: TourAudience = audienceFor(null);

  const open = (): void => {
    if (live) return;
    live = new TourOverlay({
      ...deps,
      onClose: () => {
        live = null;
        writeTourMemory(markTourSeen(readTourMemory(), audience));
      },
    });
    // A build where every anchored step was filtered out and only the two centred cards remain has
    // nothing to teach; better to say nothing than to open a tutorial about an empty screen.
    if (live.length <= 2) {
      live.dispose();
      live = null;
    }
  };

  return {
    start(): void {
      open();
    },
    offer(who: TourAudience): void {
      audience = who;
      if (!shouldOfferTour(readTourMemory(), who)) return;
      open();
    },
    dispose(): void {
      live?.dispose();
      live = null;
    },
  };
}
